import { Autocomplete, Box, Checkbox, InputAdornment, TextField, type SxProps, type Theme } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useMemo, useState } from 'react'
import { SelectionChip } from './SelectionChip'

export interface MultiSelectItem {
  id: number
  name: string
}

const SELECT_ALL: MultiSelectItem = { id: Number.NaN, name: 'Select All' }

const matchesQuery = (item: MultiSelectItem, query: string): boolean =>
  item.name.toLowerCase().includes(query.trim().toLowerCase())

const styles = {
  field: {
    maxWidth: '480px',
    '& .MuiFilledInput-root': {
      minHeight: '48px',
      padding: '0 39px 0 16px'
    },
    '& .MuiFilledInput-root .MuiAutocomplete-input': {
      padding: '13px 0'
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: 'var(--grey-300)'
    }
  } satisfies SxProps<Theme>,
  searchIcon: {
    color: 'var(--grey-500)'
  } satisfies SxProps<Theme>,
  panel: {
    border: '1px solid var(--grey-200)',
    borderRadius: '6px',
    boxShadow: '0.5px 1.5px 7px rgba(26, 26, 26, 0.08)'
  } satisfies SxProps<Theme>,
  listbox: {
    maxHeight: '288px',
    py: 0
  } satisfies SxProps<Theme>,
  option: {
    minHeight: '48px',
    px: '16px',
    gap: '10px',
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: 500,
    color: 'var(--grey-900)'
  } satisfies SxProps<Theme>,
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: '8px',
    columnGap: '24px',
    mt: '24px'
  } satisfies SxProps<Theme>
}

interface MultiSelectWithChipsProps {
  items: MultiSelectItem[]
  selectedIds: number[]
  onChange: (selectedIds: number[]) => void
  placeholder: string
  ariaLabel: string
  optionTestIdPrefix?: string
  chipTestIdPrefix?: string
}

export const MultiSelectWithChips = ({
  items, selectedIds, onChange, placeholder, ariaLabel, optionTestIdPrefix, chipTestIdPrefix
}: MultiSelectWithChipsProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedItems = useMemo(() => items.filter(item => selectedIds.includes(item.id)), [items, selectedIds])
  const visibleItems = useMemo(() => items.filter(item => matchesQuery(item, query)), [items, query])
  const selectedVisibleCount = visibleItems.filter(item => selectedIds.includes(item.id)).length
  const allVisibleSelected = visibleItems.length > 0 && selectedVisibleCount === visibleItems.length

  const toggleAllVisible = (): void => {
    const visibleIds = visibleItems.map(item => item.id)
    onChange(allVisibleSelected
      ? selectedIds.filter(id => !visibleIds.includes(id))
      : [...selectedIds, ...visibleIds.filter(id => !selectedIds.includes(id))])
  }

  const remove = (id: number): void => { onChange(selectedIds.filter(selectedId => selectedId !== id)) }

  return (
    <>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disableClearable
        open={open}
        onOpen={() => { setOpen(true) }}
        onClose={() => { setOpen(false); setQuery('') }}
        options={items}
        value={selectedItems}
        inputValue={query}
        onInputChange={(_event, value, reason) => { if (reason === 'input') setQuery(value) }}
        filterOptions={() => visibleItems.length > 0 ? [SELECT_ALL, ...visibleItems] : []}
        onChange={(_event, value, _reason, details) => {
          if (details?.option === SELECT_ALL) {
            toggleAllVisible()
            return
          }
          onChange(value.filter(item => item !== SELECT_ALL).map(item => item.id))
        }}
        // Chips render below the field instead, so Backspace in an empty search box must not
        // silently drop the last selection the way Autocomplete's built-in tags would.
        onKeyDown={event => { if (event.key === 'Backspace' && query === '') event.defaultMuiPrevented = true }}
        getOptionLabel={item => item.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderTags={() => null}
        popupIcon={<KeyboardArrowDownIcon />}
        noOptionsText='No matches'
        sx={styles.field}
        slotProps={{ paper: { sx: styles.panel } }}
        ListboxProps={{ sx: styles.listbox }}
        renderOption={(optionProps, item, { selected }) => {
          const { key, ...liProps } = optionProps as typeof optionProps & { key: string }
          const isSelectAll = item === SELECT_ALL
          return (
            <Box
              component='li'
              key={key}
              {...liProps}
              sx={styles.option}
              data-testid={optionTestIdPrefix != null ? `${optionTestIdPrefix}-${isSelectAll ? 'all' : item.id}` : undefined}
            >
              <Checkbox
                tabIndex={-1}
                disableRipple
                checked={isSelectAll ? allVisibleSelected : selected}
                indeterminate={isSelectAll && selectedVisibleCount > 0 && !allVisibleSelected}
              />
              {item.name}
            </Box>
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={open ? 'Search' : placeholder}
            inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
            InputProps={{
              ...params.InputProps,
              startAdornment: open
                ? <InputAdornment position='start'><SearchIcon sx={styles.searchIcon} /></InputAdornment>
                : undefined
            }}
          />
        )}
      />
      {selectedItems.length > 0 &&
        <Box sx={styles.chips}>
          {selectedItems.map(item => (
            <SelectionChip
              key={item.id}
              label={item.name}
              onDelete={() => { remove(item.id) }}
              dataTestId={chipTestIdPrefix != null ? `${chipTestIdPrefix}-${item.id}` : undefined}
            />
          ))}
        </Box>}
    </>
  )
}
